import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { CreditoDetailDrawer } from './CreditoDetailDrawer';
import { RegistrarPagoCreditoModal } from './RegistrarPagoCreditoModal';
import type { CuotaLike } from './creditosUiHelpers';
import { badgeToneListado, labelEstadoCredito, proximaCuotaPendiente } from './creditosUiHelpers';

const fmtCop = (n: number) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(n);

type CreditoDb = {
  id: string;
  cliente_id: string;
  tipo: string;
  monto_total: number;
  saldo_pendiente: number;
  estado: string;
  fecha_vencimiento: string | null;
  created_at: string;
  updated_at?: string;
};

type Props = {
  empresaId: string;
  clienteId: string;
  onRefreshParent?: () => void;
  /** En el asistente de factura: menos altura, sin tarjetas KPI grandes. */
  layout?: 'full' | 'compact';
};

export const ClienteCreditosTab: React.FC<Props> = ({
  empresaId,
  clienteId,
  onRefreshParent,
  layout = 'full',
}) => {
  const compact = layout === 'compact';
  const [creditos, setCreditos] = useState<CreditoDb[]>([]);
  const [cuotasPorCredito, setCuotasPorCredito] = useState<Record<string, CuotaLike[]>>({});
  const [loading, setLoading] = useState(true);
  const [histOpen, setHistOpen] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);
  const [pagoModalCreditoId, setPagoModalCreditoId] = useState<string | null>(null);
  const [pagoPickerOpen, setPagoPickerOpen] = useState(false);

  const load = useCallback(async () => {
    if (!empresaId || !clienteId) return;
    setLoading(true);
    let list: CreditoDb[] = [];

    try {
      const { data: crs, error } = await supabase
        .from('creditos')
        .select('id, cliente_id, tipo, monto_total, saldo_pendiente, estado, fecha_vencimiento, created_at, updated_at')
        .eq('empresa_id', empresaId)
        .eq('cliente_id', clienteId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      list = (crs || []) as CreditoDb[];
    } catch (e) {
      console.error('[Cliente créditos] Error al cargar créditos:', e);
      setCreditos([]);
      setCuotasPorCredito({});
      setLoading(false);
      return;
    }

    setCreditos(list);
    const ids = list.map((c) => c.id);
    if (ids.length === 0) {
      setCuotasPorCredito({});
      setLoading(false);
      return;
    }

    try {
      const { data: cuAll, error: e2 } = await supabase
        .from('cuotas')
        .select('id, credito_id, numero_cuota, monto_cuota, monto_pagado, fecha_vencimiento, estado')
        .eq('empresa_id', empresaId)
        .in('credito_id', ids);
      if (e2) throw e2;
      const by: Record<string, CuotaLike[]> = {};
      for (const q of cuAll || []) {
        const cid = (q as { credito_id: string }).credito_id;
        if (!by[cid]) by[cid] = [];
        by[cid]!.push(q as CuotaLike);
      }
      Object.keys(by).forEach((k) => {
        const arr = by[k];
        if (arr) arr.sort((a, b) => a.numero_cuota - b.numero_cuota);
      });
      setCuotasPorCredito(by);
    } catch (e) {
      console.error('[Cliente créditos] Error al cargar cuotas (se muestran créditos sin detalle de cuotas):', e);
      setCuotasPorCredito({});
    }

    setLoading(false);
  }, [empresaId, clienteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const activos = useMemo(
    () => creditos.filter((c) => c.estado === 'activo' || c.estado === 'vencido'),
    [creditos]
  );
  const cerradosList = useMemo(
    () => creditos.filter((c) => c.estado === 'pagado' || c.estado === 'cancelado'),
    [creditos]
  );

  const resumen = useMemo(() => {
    let saldo = 0;
    let vencidos = 0;
    for (const c of activos) {
      saldo += Number(c.saldo_pendiente) || 0;
      const tone = badgeToneListado(c.estado, cuotasPorCredito[c.id] || []);
      if (tone === 'red') vencidos += 1;
    }
    return { saldo, n: activos.length, vencidos };
  }, [activos, cuotasPorCredito]);

  const abrirRegistrarPago = () => {
    if (activos.length === 0) return;
    if (activos.length === 1) {
      setPagoModalCreditoId(activos[0]!.id);
      return;
    }
    setPagoPickerOpen(true);
  };

  if (loading) {
    return (
      <div className={compact ? 'py-4 text-center text-xs text-gray-500' : 'py-10 text-center text-sm text-gray-500'}>
        <div
          className={clsx(
            'mx-auto animate-spin rounded-full border-2 border-blue-600 border-t-transparent',
            compact ? 'h-5 w-5' : 'h-8 w-8'
          )}
        />
        <p className="mt-2">Cargando créditos…</p>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-4'}>
      {compact ? (
        <p className="text-[11px] text-gray-600">
          Otros créditos del cliente: <span className="font-medium text-gray-900">{fmtCop(resumen.saldo)}</span> en{' '}
          <span className="font-medium">{resumen.n}</span> activo(s)
          {resumen.vencidos > 0 ? (
            <span className="text-red-600"> · {resumen.vencidos} vencido(s)</span>
          ) : null}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
            <p className="text-xs font-semibold uppercase text-gray-500">Saldo pendiente</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{fmtCop(resumen.saldo)}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
            <p className="text-xs font-semibold uppercase text-gray-500">Créditos activos</p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{resumen.n}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
            <p className="text-xs font-semibold uppercase text-gray-500">Alerta vencidos</p>
            <p className={`mt-1 text-lg font-semibold ${resumen.vencidos > 0 ? 'text-red-700' : 'text-gray-900'}`}>
              {resumen.vencidos}
            </p>
          </div>
        </div>
      )}

      {activos.length > 0 && !compact ? (
        <button
          type="button"
          className="w-full rounded-lg py-2.5 text-sm font-medium text-white sm:w-auto sm:px-6"
          style={{ backgroundColor: '#1A56DB' }}
          onClick={abrirRegistrarPago}
        >
          Registrar pago
        </button>
      ) : null}

      <div>
        <h3 className={clsx('font-semibold text-gray-900', compact ? 'mb-1 text-[11px]' : 'mb-2 text-sm')}>
          Créditos activos
        </h3>
        {activos.length === 0 ? (
          <p className="text-sm text-gray-500">Sin créditos activos.</p>
        ) : (
          <ul
            className={clsx(
              'divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100',
              compact && 'max-h-32 overflow-y-auto'
            )}
          >
            {activos.map((c) => {
              const cu = cuotasPorCredito[c.id] || [];
              const prox = proximaCuotaPendiente(cu);
              const sinCuotasEnCache = cu.length === 0;
              const proxTxt =
                prox != null
                  ? `${prox.fecha_vencimiento} (#${prox.numero_cuota})`
                  : c.tipo === 'simple' && c.fecha_vencimiento
                    ? c.fecha_vencimiento
                    : sinCuotasEnCache && c.tipo === 'plan_pagos'
                      ? 'Sin datos'
                      : '—';
              const tone = badgeToneListado(c.estado, cu);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col gap-1 px-3 py-2.5 text-left text-sm hover:bg-gray-50"
                    onClick={() => setDetalleId(c.id)}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={
                          c.tipo === 'simple'
                            ? 'rounded bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-700'
                            : 'rounded bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-indigo-800'
                        }
                      >
                        {c.tipo === 'simple' ? 'Simple' : 'Plan de pagos'}
                      </span>
                      <span
                        className={
                          tone === 'red'
                            ? 'rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-800'
                            : tone === 'yellow'
                              ? 'rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900'
                              : 'rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-900'
                        }
                      >
                        {labelEstadoCredito(c.estado)}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">
                      Total {fmtCop(Number(c.monto_total) || 0)} · Saldo {fmtCop(Number(c.saldo_pendiente) || 0)}
                    </p>
                    <p className="text-xs text-gray-500">Próximo venc.: {proxTxt}</p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-gray-200">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm font-medium text-gray-800"
          onClick={() => setHistOpen(!histOpen)}
        >
          <span>Historial (pagados / cancelados)</span>
          {histOpen ? <ChevronDownIcon className="h-5 w-5 text-gray-500" /> : <ChevronRightIcon className="h-5 w-5 text-gray-500" />}
        </button>
        {histOpen && (
          <div className="border-t border-gray-100 px-3 py-2">
            {cerradosList.length === 0 ? (
              <p className="text-sm text-gray-500">Sin historial.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {cerradosList.map((c) => (
                  <li key={c.id} className="flex justify-between gap-2 text-gray-700">
                    <span>
                      {c.tipo === 'simple' ? 'Simple' : 'Plan'} · {labelEstadoCredito(c.estado)}
                    </span>
                    <span className="font-medium">{fmtCop(Number(c.monto_total) || 0)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <CreditoDetailDrawer
        open={detalleId != null}
        onClose={() => setDetalleId(null)}
        empresaId={empresaId}
        creditoId={detalleId}
        onChanged={() => {
          void load();
          onRefreshParent?.();
        }}
      />

      <RegistrarPagoCreditoModal
        open={pagoModalCreditoId != null}
        onClose={() => setPagoModalCreditoId(null)}
        empresaId={empresaId}
        creditoId={pagoModalCreditoId}
        onSuccess={() => {
          void load();
          onRefreshParent?.();
        }}
      />

      {pagoPickerOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl">
            <p className="text-sm font-medium text-gray-900">¿A qué crédito aplica el pago?</p>
            <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto">
              {activos.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-left text-sm hover:bg-gray-50"
                    onClick={() => {
                      setPagoModalCreditoId(c.id);
                      setPagoPickerOpen(false);
                    }}
                  >
                    {c.tipo === 'simple' ? 'Simple' : 'Plan'} · {fmtCop(Number(c.saldo_pendiente) || 0)} pend.
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="mt-3 w-full rounded-lg border border-gray-300 py-2 text-sm text-gray-700"
              onClick={() => setPagoPickerOpen(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
